import { useState, useCallback, useRef, useEffect } from 'react';

const SIDEBAR_SELECTOR = '.columns-area__panels__pane--navigational';
const SIDEBAR_STATE_CHANGE_EVENT = 'sidebarStateChange';

const isSidebarActuallyVisible = () =>
  document.body.classList.contains('sidebar-visible') ||
  document.querySelector(`${SIDEBAR_SELECTOR}.active`) !== null;

export interface SidebarState {
  isVisible: boolean;
  isTransitioning: boolean;
  toggleSidebar: () => void;
  closeSidebar: () => void;
  openSidebar: () => void;
}

export const useSidebar = (): SidebarState => {
  const [isVisible, setIsVisible] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const scrollPositionRef = useRef(0);
  const overlayRef = useRef<HTMLDivElement | null>(null);

  const createOverlay = useCallback(() => {
    const overlay = document.createElement('div');
    overlay.className = 'sidebar-overlay';
    overlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-color: rgba(24, 24, 33, 0.5);
      z-index: 50;
      opacity: 0;
      transition: opacity 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
      backdrop-filter: blur(4px);
      -webkit-backdrop-filter: blur(4px);
    `;
    document.body.appendChild(overlay);
    overlayRef.current = overlay;

    // overlay.addEventListener('click', () => {
    //     closeSidebar();
    // });

    requestAnimationFrame(() => {
      overlay.style.opacity = '1';
    });

    return overlay;
  }, []);

  const removeOverlay = useCallback(() => {
    if (overlayRef.current) {
      overlayRef.current.style.opacity = '0';
      overlayRef.current.remove();
      overlayRef.current = null;
    }
    document.querySelectorAll('.sidebar-overlay').forEach(el => {
      el.remove();
    });
  }, []);

  const closeSidebar = useCallback(() => {
    if (!isSidebarActuallyVisible() && !isVisible) {
      removeOverlay();
      setIsVisible(false);
      setIsTransitioning(false);
      return;
    }

    setIsTransitioning(true);

    document.body.classList.add('sidebar-closing');
    const sidebar = document.querySelector(SIDEBAR_SELECTOR);

    sidebar?.classList.add('sidebar-slide-out');

    removeOverlay();

    setTimeout(() => {
      document.body.classList.remove('sidebar-visible', 'sidebar-closing');
      sidebar?.classList.remove('active', 'sidebar-slide-out');

      document.body.style.cssText = '';
      window.scrollTo(0, scrollPositionRef.current);

      setIsVisible(false);
      setIsTransitioning(false);
      document.dispatchEvent(new CustomEvent(SIDEBAR_STATE_CHANGE_EVENT, { detail: { visible: false } }));
    }, 300);
  }, [isVisible, removeOverlay]);

  const openSidebar = useCallback(() => {
    if (isTransitioning || isSidebarActuallyVisible()) return;

    setIsTransitioning(true);
    scrollPositionRef.current = window.scrollY;

    document.body.style.cssText = `
      position: fixed;
      top: -${scrollPositionRef.current}px;
      width: 100%;
      overflow: hidden;
    `;

    const overlay = createOverlay();

    document.body.classList.add('sidebar-visible');
    const sidebar = document.querySelector(SIDEBAR_SELECTOR);
    if (sidebar) {
      sidebar.classList.add('active');
    }
    overlay.addEventListener('click', closeSidebar);

    setIsVisible(true);
    document.dispatchEvent(new CustomEvent(SIDEBAR_STATE_CHANGE_EVENT, { detail: { visible: true } }));
    setTimeout(() => {
      setIsTransitioning(false);
    }, 300);
  }, [closeSidebar, createOverlay, isTransitioning]);

  const toggleSidebar = useCallback(() => {
    if (isTransitioning) return;
    if (isSidebarActuallyVisible()) {
      closeSidebar();
    } else {
      openSidebar();
    }
  }, [isTransitioning, openSidebar, closeSidebar]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isVisible) {
        closeSidebar();
      }
    };

    const handleToggleEvent = (e: Event) => {
      const visible = (e as CustomEvent<{ visible?: boolean }>).detail.visible;

      if (visible === false) {
        closeSidebar();
      } else if (visible === true) {
        openSidebar();
      } else if (isSidebarActuallyVisible()) {
        closeSidebar();
      } else {
        openSidebar();
      }
    };

    const handleSidebarStateChange = (e: Event) => {
      const visible = (e as CustomEvent<{ visible?: boolean }>).detail.visible;

      if (typeof visible === 'boolean') {
        setIsVisible(visible);
        if (!visible) {
          setIsTransitioning(false);
        }
      }
    };

    const handleOutsideClick = (e: MouseEvent) => {
      if (!isVisible || isTransitioning) return;

      const target = e.target as HTMLElement | null;

      if (target?.closest('.column-header__sidebar-toggle')) {
        return;
      }

      const sidebar = document.querySelector('.columns-area__panels__pane--navigational');
      const overlay = overlayRef.current;

      if (
        sidebar &&
        overlay &&
        !sidebar.contains(e.target as Node) &&
        !overlay.contains(e.target as Node)
      ) {
        closeSidebar();
      }
    };

    document.addEventListener('keydown', handleEscape);
    document.addEventListener('toggleSidebar', handleToggleEvent);
    document.addEventListener(SIDEBAR_STATE_CHANGE_EVENT, handleSidebarStateChange);
    document.addEventListener('click', handleOutsideClick);

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('toggleSidebar', handleToggleEvent);
      document.removeEventListener(SIDEBAR_STATE_CHANGE_EVENT, handleSidebarStateChange);
      document.removeEventListener('click', handleOutsideClick);
    };
  }, [closeSidebar, openSidebar, isTransitioning, isVisible]);

  return {
    isVisible,
    isTransitioning,
    toggleSidebar,
    closeSidebar,
    openSidebar,
  };
};
